import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { client } from "@johnpaul/jp-api-client";
import { getClientTokenFn, getHashedPassword } from "@companion-core/shared/app/Utils/apiClient";
import { getClientCredentials } from "@companion-core/shared/app/Utils/config";
import { AxiosError } from "axios";
import { errorString } from "../Constants/errors";

// Token requests

export const useAuthClientToken = (enabled: boolean) => {
  return useQuery({
    queryKey: ["authClientToken"],
    queryFn: getClientTokenFn,
    enabled,
  });
};

export const useAuthTokenSSOClient = (authCode: string, redirectUri: string, enabled = true) => {
  return useQuery({
    queryKey: ["authTokenSSOClient"],
    queryFn: () =>
      client.auth.v2.getTokenSSOClient(
        getClientCredentials().clientId,
        getClientCredentials().clientSecret,
        authCode,
        redirectUri
      ),
    enabled,
    retry: false,
    gcTime: 0,
  });
};

type LinkCodeParams = { linkCode: string };

export const useAuthTokenLinkCodeClient = (callbacks?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) => {
  const mutationFn: MutationFunction<unknown, LinkCodeParams> = ({ linkCode }) =>
    client.auth.v2.getTokenFromLinkCode(
      getClientCredentials().clientId,
      getClientCredentials().clientSecret,
      linkCode
    );

  return useMutation({
    mutationFn,
    retry: false,
    gcTime: 0,
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
  });
};

// Update Password request
export type ChangePasswordPayload = {
  username: string;
  password: string;
  newPassword: string;
  alreadyHashed: boolean;
};

type ChangePasswordParams = {
  payload: ChangePasswordPayload;
};

interface UpdatePasswordErrorDescription {
  password: errorString[];
  new_password: errorString[];
  previous_password: errorString[];
}

export interface UpdatePasswordError {
  error: string;
  error_description: UpdatePasswordErrorDescription;
}

export const useChangePassword = (onError?: (error: AxiosError<UpdatePasswordError>) => void) => {
  const mutationFn: MutationFunction<unknown, ChangePasswordParams> = async ({ payload }) => {
    const { username, password, newPassword } = payload;
    payload.password = await getHashedPassword(username, password, true);
    payload.newPassword = await getHashedPassword(username, newPassword, true);
    payload.alreadyHashed = true;
    await client.password.update(payload, true);
    return payload;
  };

  return useMutation({
    mutationFn,
    retry: false,
    gcTime: 0,
    onError: onError ? onError : undefined,
  });
};

export type ChangeSecretPayload = {
  username: string;
  password: string;
  secretQuestion: string;
  secretAnswer: string;
  alreadyHashed: boolean;
};

type ChangeSecretParams = {
  payload: ChangeSecretPayload;
};

export const useChangeSecret = (onError?: () => void) => {
  const mutationFn: MutationFunction<unknown, ChangeSecretParams> = async ({ payload }) => {
    const { username, password, secretAnswer } = payload;
    payload.password = await getHashedPassword(username, password, true);
    payload.secretAnswer = await getHashedPassword(username, secretAnswer, true);
    payload.alreadyHashed = true;
    return await client.members.secret.update(payload);
  };

  return useMutation({
    mutationFn,
    retry: false,
    gcTime: 0,
    onError: onError ? onError : undefined,
  });
};

// SSO Partner

export const useAuthSSOInfos = (clientId: string) => {
  return useQuery({
    queryKey: ["authSSOInfos", clientId],
    queryFn: () => client.auth.v2.getSSOInfos(clientId),
    retry: false,
    gcTime: 0,
  });
};

type AuthSSOParams = {
  client_id: string;
  response_type: string;
  redirect_uri: string;
  state: string;
  scope: string;
  code_challenge: string;
  code_challenge_method: string;
};

type Data = {
  code: string;
  redirect_uri: string;
};

type AuthSSOResponse = {
  data: Data;
};

export const useAuthSSO = (onError?: () => void) => {
  const mutationFn: MutationFunction<AuthSSOResponse, AuthSSOParams> = async (payload) => {
    return await client.auth.v2.authorize(payload);
  };

  return useMutation({
    mutationFn,
    retry: false,
    gcTime: 0,
    onError: onError ? onError : undefined,
  });
};
